import { create } from 'zustand';

// Bible reader Study Mode toggle. Global persisted boolean — when on,
// lookupable words in the Bible reader glow gold and tap opens the
// definition sheet (see src/lib/glossary.ts + GlossarySheet).
//
// Persisted to localStorage under 'adia-study-mode' (Option-A naming
// convention from the watchHim.work rebrand — internal storage keys
// stay 'adia-*' to preserve existing user state). Init runs at module
// load, BEFORE any component mounts, so the first BibleChapter render
// already reflects the user's preference.

const STORAGE_KEY = 'adia-study-mode';

function readInitial(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // Private-mode Safari can throw on localStorage reads; fall back to off.
    return false;
  }
}

function persist(value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Silently swallow; the in-memory state still drives the UI for this session.
  }
}

interface StudyModeState {
  enabled: boolean;
  setEnabled: (value: boolean) => void;
  toggle: () => void;
}

export const useStudyModeStore = create<StudyModeState>((set, get) => ({
  enabled: readInitial(),
  setEnabled: (value) => {
    persist(value);
    set({ enabled: value });
  },
  toggle: () => {
    const next = !get().enabled;
    persist(next);
    set({ enabled: next });
  },
}));
